import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';

interface TourStep {
  target: string;
  content: string;
  disableBeacon?: boolean;
}

interface Tour {
  steps: TourStep[];
}

interface Tours {
  [key: string]: Tour;
}

// ツアーの定義
const tours: Tours = {
  dashboard: {
    steps: [
      {
        target: '.dashboard-overview',
        content: 'ダッシュボードの概要を確認できます。',
        disableBeacon: true,
      },
      {
        target: '.recent-orders',
        content: '最近のオーダー情報を表示しています。',
      },
    ]
  },
  shifts: {
    steps: [
      {
        target: '.shift-calendar',
        content: 'シフトの予定を月単位で確認できます。',
      },
      {
        target: '.shift-list',
        content: '担当するシフトの詳細を確認できます。',
      }
    ]
  },
  orders: {
    steps: [
      {
        target: '.order-list',
        content: '担当する配送オーダーの一覧です。',
      },
      {
        target: '.order-map',
        content: '配送先の位置をマップで確認できます。',
      }
    ]
  }
};

interface TourContextType {
  isActive: boolean;
  setIsActive: (active: boolean) => void;
  completedTours: string[];
  markTourAsCompleted: (tourId: string) => void;
}

interface TourProviderProps {
  children: React.ReactNode;
}

// ツアーコンテキストの作成
const TourContext = createContext<TourContextType | undefined>(undefined);

// ツアーコンテキストプロバイダーコンポーネント
export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [completedTours, setCompletedTours] = useState<string[]>(() => {
    const saved = localStorage.getItem('completedTours');
    return saved ? JSON.parse(saved) : [];
  });

  const markTourAsCompleted = (tourId: string) => {
    setCompletedTours(prev => {
      const updated = [...prev, tourId];
      localStorage.setItem('completedTours', JSON.stringify(updated));
      return updated;
    });
  };

  const value = {
    isActive,
    setIsActive,
    completedTours,
    markTourAsCompleted,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

// カスタムフック
export const useTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

// パスに基づいてツアーを取得
const getTourByPath = (path: string): Tour | null => {
  const tourMap: Record<string, Tour> = {
    '/dashboard': tours.dashboard,
    '/shifts': tours.shifts,
    '/orders': tours.orders
  };
  return tourMap[path] || null;
};

// 高階コンポーネント
export const withTourContext = <P extends object>(
  WrappedComponent: React.ComponentType<P>
): React.FC<P> => {
  const WithTourComponent = (props: P) => {
    return (
      <TourProvider>
        <WrappedComponent {...props} />
      </TourProvider>
    );
  };
  
  // 表示名を設定
  const wrappedComponentName = WrappedComponent.displayName 
    || WrappedComponent.name 
    || 'Component';
  WithTourComponent.displayName = `WithTour(${wrappedComponentName})`;
  
  return WithTourComponent;
};

export default TourContext;
